import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { directShipApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';
import { DirectShipToPanel } from '@/components/ewayBill/DirectShipToPanel';

const STATE = { no_to: () => 'not on a Transfer Out yet', no_bill: (b) => `on ${b.to_doc_no} · no e-way bill yet`, waiting: (b) => `on ${b.to_doc_no} · e-way bill waiting for the portal` };

/** Direct Ship screen: upload the Ginesys Transfer Out + tick its boxes, and the boxes (last 15 days) without an Active e-way bill. */
export const DirectShipEwbPending = ({ tick }) => {
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(false);
  useEffect(() => { directShipApi.pending().then(setR).catch(() => setR(null)); }, [tick, panel]);
  const n = r?.count || 0;
  return (
    <div className={`rounded-xl border p-3 ${n ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`} data-testid="ds-ewb-pending">
      <div className="flex flex-wrap items-center gap-2">
        <FileWarning className={`w-5 h-5 ${n ? 'text-amber-700' : 'text-gray-400'}`} />
        <div className="text-sm text-gray-800">
          {n ? <><b data-testid="ds-ewb-pending-count">{n}</b> box(es) to our sites have no e-way bill yet <span className="text-amber-700">(last {r.days} days)</span></>
            : 'E-way bill: upload the Ginesys Transfer Out and tick its boxes'}
        </div>
        {n > 0 && <Button size="sm" variant="ghost" className="h-8 text-amber-900" onClick={() => setOpen(!open)} data-testid="ds-ewb-pending-toggle">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}{open ? 'Hide' : 'Show'}</Button>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setPanel(true)} data-testid="ds-to-open-btn">Transfer Out & boxes</Button>
          {n > 0 && <Button size="sm" className="h-8 bg-[#1A1F2E] hover:bg-black" onClick={() => navigate('/eway-bills')} data-testid="ds-ewb-open-btn">Make e-way bill</Button>}
        </div>
      </div>
      {open && n > 0 && (
        <ul className="mt-2 max-h-52 divide-y overflow-y-auto rounded-lg border border-amber-200 bg-white text-xs" data-testid="ds-ewb-pending-list">
          {r.boxes.map((b) => (
            <li key={b.box_number} className="flex items-center gap-2 px-3 py-1.5" data-testid={`ds-pending-${b.box_number}`}>
              <span className="font-mono font-semibold">{b.box_number}</span><span>→ {b.destination_name}</span>
              <span className="rounded-full bg-amber-100 px-2 text-amber-800">{STATE[b.state](b)}</span>
              <span className="ml-auto text-gray-500">{fmtDT(b.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={panel} onOpenChange={setPanel}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ds-to-dialog">
          <DialogHeader><DialogTitle>Direct Ship — Transfer Out & boxes</DialogTitle></DialogHeader>
          {panel && <DirectShipToPanel />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectShipEwbPending;
